import axios from "axios";
import i18next from "i18next";
import { API_URL, CLIENT_TYPE, TIMEZONE } from "../constant";
import {
  getAuthUserData,
  setAuthUserData,
  removeAuthUserData,
} from "../localStorage";
import { loginUser, logoutUser } from "../redux/auth/authActions";
import store from "../redux/store";
import { toast } from "react-toastify";

// Cerrar todas las alertas activas antes de ejecutar la alerta de sesión expirada
const showToast = () => {
  toast.dismiss();
  toast.error(i18next.t("session_expired"), {
    toastId: "session_expired",
    theme: "colored",
    autoClose: 3000,
  });
};

// Promesa compartida del refresh en curso: todas las solicitudes que fallen mientras se
// refresca el token esperan a esta misma promesa (y fallan juntas si el refresh falla)
let refreshPromise = null;

const forceLogout = (showExpiredToast) => {
  refreshPromise = null;
  store.dispatch(logoutUser());
  removeAuthUserData();
  if (showExpiredToast) {
    showToast();
  }
};

const doRefresh = async (failedAccessToken) => {
  // Otra pestaña pudo haber refrescado ya el token (los tokens se comparten vía localStorage);
  // como los refresh tokens rotan y se invalidan, reutilizar el nuevo en lugar de refrescar otra vez
  const current = getAuthUserData();
  if (current?.access_token && current.access_token !== failedAccessToken) {
    return current.access_token;
  }
  if (!current?.refresh_token) {
    throw new Error("No refresh token");
  }

  const response = await refreshAccessToken({ refresh: current.refresh_token });
  const updatedUserData = {
    ...current,
    access_token: response.data.access,
    refresh_token: response.data.refresh,
  };
  setAuthUserData(updatedUserData);
  store.dispatch(loginUser(updatedUserData));
  return updatedUserData.access_token;
};

const getRefreshedAccessToken = (failedAccessToken) => {
  if (!refreshPromise) {
    // Serializar el refresh entre pestañas cuando el navegador lo soporta
    const run = () => doRefresh(failedAccessToken);
    refreshPromise = (
      navigator.locks?.request
        ? navigator.locks.request("ambumedik-token-refresh", run)
        : run()
    ).finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

// Crear una instancia de axios
const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    "X-Client-Type": CLIENT_TYPE,
    "Timezone": TIMEZONE
  }
});

// Usar un interceptor de solicitud para agregar el access_token a cada solicitud
axiosInstance.interceptors.request.use(
  (config) => {
    // Lista de endpoints que no requieren access_token
    const publicEndpoints = ["accounts/login/", "token/refresh/"];

    // Verificar si la URL del endpoint está en la lista de endpoints públicos
    if (!publicEndpoints.includes(config.url)) {
      // Obtener el usuario autenticado
      const userData = getAuthUserData();
      const access_token = userData ? userData.access_token : null;

      // Agregar el access_token en la cabecera de cada solicitud de forma automática
      if (access_token) {
        config.headers.Authorization = `Bearer ${access_token}`;
      }
    }

    // Agregar el encabezado de tipo de cliente
    config.headers["X-Client-Type"] = CLIENT_TYPE;

    // Agregar el encabezado de la zona horaria
    config.headers["Timezone"] = TIMEZONE;

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor de respuesta para manejar el error de access_token no válido
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config || {};
    const errorCode = error.response?.data?.code;
    const userData = getAuthUserData();
    const refresh_token = userData ? userData.refresh_token : null;

    if (!refresh_token || errorCode === "refresh_token_not_valid") {
      // Si el refresh del token de acceso falla, hacer logout
      forceLogout(errorCode === "refresh_token_not_valid");
      return Promise.reject(error);
    }

    // Reintentar una sola vez para evitar bucles de refresh
    if (
      errorCode === "token_not_valid" &&
      !originalRequest._retry &&
      originalRequest.url !== "token/refresh/"
    ) {
      originalRequest._retry = true;
      const failedAccessToken = (originalRequest.headers?.Authorization || "").replace(
        "Bearer ",
        ""
      );
      try {
        const newAccessToken = await getRefreshedAccessToken(failedAccessToken);
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        // El refresh falló: cerrar sesión y rechazar (antes la solicitud quedaba pendiente para siempre)
        if (getAuthUserData()) {
          forceLogout(true);
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;

//----------------------------------------------------------------------

// Endpoints
// FCM Device
export const createOrUpdateFCMDevice = (data) => {
  return axiosInstance.post("fcm-devices/", data);
};

// Iniciar sesión
export const login = ({ email, password }) => {
  return axiosInstance.post("accounts/login/", { email, password });
};

export const changePassword = ({
  old_password,
  new_password1,
  new_password2,
}) => {
  return axiosInstance.post("accounts/password-change/", {
    old_password,
    new_password1,
    new_password2,
  });
};

// Refrescar token de acceso
export const refreshAccessToken = ({ refresh }) => {
  return axiosInstance.post("token/refresh/", { refresh });
};

// Endpoints para las ambulancias ------------------------------------------------------
// Obtener listado de ambulancias
export const getAmbulances = (params) => {
  const queryParams = { ...params };
  return axiosInstance.get("ambulances/", { params: queryParams });
};
// Obtener listado de ambulancias disponibles para su asignación en las solicitudes
export const getAvailableAmbulances = (user_request) => {
  const param = user_request ? `?user_request_id=${user_request}` : "";
  return axiosInstance.get(`available-ambulances/${param}`);
};

// Obtener una ambulancia
export const getAmbulance = (id) => {
  return axiosInstance.get(`ambulances/${id}/`);
};

// Crear una ambulancia
export const createAmbulance = (ambulanceData) => {
  return axiosInstance.post("ambulances/", ambulanceData);
};

// Actualizar una ambulancia
export const updateAmbulance = ({ id, ambulanceData }) => {
  return axiosInstance.patch(`ambulances/${id}/`, ambulanceData);
};

// Borrar una ambulancia
export const deleteAmbulance = (id) => {
  return axiosInstance.delete(`ambulances/${id}/`);
};

// Endpoints para las rutas ------------------------------------------------------
// Obtener listado de rutas
export const getRoutes = (params) => {
  const queryParams = { ...params };
  return axiosInstance.get("routes/", { params: queryParams });
};

// Obtener una ruta
export const getRoute = (id) => {
  return axiosInstance.get(`routes/${id}/`);
};

// Obtener estados de las rutas
export const getRouteStatus = (params) => {
  const queryParams = { ...params };
  return axiosInstance.get(`route-status/`, { params: queryParams });
};

// Borrar una ruta
export const deleteRoute = (id) => {
  return axiosInstance.delete(`routes/${id}/`);
};

// Tracking de la ruta
// Asegurar que se carguen todos los datos
export const getRouteTracking = (id) => {
  return axiosInstance.get(`route-tracking/?route=${id}`, {
    params: { page_size: 10000 },
  });
};

// Endpoints para los roles ------------------------------------------------------
// Obtener listado de plataformas
export const getPlatform = () => {
  return axiosInstance.get("platforms/");
};

// Obtener listado de roles
export const getRoles = (params) => {
  const queryParams = { ...params };
  return axiosInstance.get("accounts/groups/", { params: queryParams });
};

// Obtener un rol
export const getRole = (id) => {
  return axiosInstance.get(`accounts/groups/${id}/`);
};

// Crear un rol
export const createRole = (roleData) => {
  return axiosInstance.post("accounts/groups/", roleData);
};

// Actualizar un rol
export const updateRole = ({ id, roleData }) => {
  return axiosInstance.patch(`accounts/groups/${id}/`, roleData);
};

// Borrar un rol
export const deleteRole = (id) => {
  return axiosInstance.delete(`accounts/groups/${id}/`);
};

// Obtener todos los permisos
export const getPermissions = () => {
  return axiosInstance.get("accounts/available-permissions/");
};

// Obtener los permisos disponibles para un rol específico.
export const getAvailablePermissions = (id) => {
  return axiosInstance.get(`accounts/available-permissions/${id}/`);
};

// Endpoints para los servicios ------------------------------------------------------
// Obtener listado de servicios
export const getServices = (params) => {
  const queryParams = { ...params };
  return axiosInstance.get("services/", { params: queryParams });
};

// Obtener un servicio
export const getService = (id) => {
  return axiosInstance.get(`services/${id}/`);
};

// Crear un servicio
export const createService = (data) => {
  return axiosInstance.post("services/", data);
};

// Actualizar un servicio
export const updateService = ({ id, data }) => {
  return axiosInstance.patch(`services/${id}/`, data);
};

// Borrar un servicio
export const deleteService = (id) => {
  return axiosInstance.delete(`services/${id}/`);
};

// Endpoints para los tipos de servicios ------------------------------------------------------
// Obtener listado de tipos de servicios
export const getServiceTypes = (params) => {
  const queryParams = { ...params };
  return axiosInstance.get("service-type/", { params: queryParams });
};

// Obtener un tipo de servicio
export const getServiceType = (id) => {
  return axiosInstance.get(`service-type/${id}/`);
};

// Crear un tipo de servicio
export const createServiceType = (data) => {
  return axiosInstance.post("service-type/", data);
};

// Actualizar un tipo de servicio
export const updateServiceType = ({ id, data }) => {
  return axiosInstance.patch(`service-type/${id}/`, data);
};

// Borrar un tipo de servicio
export const deleteServiceType = (id) => {
  return axiosInstance.delete(`service-type/${id}/`);
};

// Endpoints para los subtipo de servicios ------------------------------------------------------
// Obtener listado de subtipo de servicios
export const getServiceSubTypes = (params) => {
  const queryParams = { ...params };
  return axiosInstance.get("service-subtype/", { params: queryParams });
};

// Obtener un subtipo de servicio
export const getServiceSubType = (id) => {
  return axiosInstance.get(`service-subtype/${id}/`);
};

// Obtener listado de subtipos de servicio asociados a un tipo de servicio
export const getServiceSubTypesByServiceType = ({ typeId }) => {
  return axiosInstance.get(`/get_subtypes/?type_id=${typeId}`);
};

// Crear un subtipo de servicio
export const createServiceSubType = (data) => {
  return axiosInstance.post("service-subtype/", data);
};

// Actualizar un subtipo de servicio
export const updateServiceSubType = ({ id, data }) => {
  return axiosInstance.patch(`service-subtype/${id}/`, data);
};

// Borrar un subtipo de servicio
export const deleteServiceSubType = (id) => {
  return axiosInstance.delete(`service-subtype/${id}/`);
};

// Endpoints para los usuarios ------------------------------------------------------
// Obtener listado de usuarios
export const getUsers = (params) => {
  return axiosInstance.get("accounts/users/", { params });
};

// Obtener un usuario
export const getUser = (id) => {
  return axiosInstance.get(`accounts/users/${id}/`);
};

// Crear un usuario
export const createUser = (userData) => {
  return axiosInstance.post("accounts/users/", userData);
};

// Actualizar un usuario
export const updateUser = ({ id, userData }) => {
  return axiosInstance.patch(`accounts/users/${id}/`, userData);
};

// Borrar un usuario
export const deleteUser = (id) => {
  return axiosInstance.delete(`accounts/users/${id}/`);
};

// Eliminar múltiples usuarios
export const deleteMultipleUsers = (user_ids) => {
  return axiosInstance.post(`accounts/delete-multiple-users/`, user_ids);
};

// Desactivar múltiples usuarios
export const activateDeactivateMultipleUsers = (data) => {
  return axiosInstance.post(`accounts/activate-deactivate-users/`, data);
};

// Obtener listado de usuarios con el rol de "Paramédico"
export const getParamedicList = (params) => {
  return axiosInstance.get("accounts/paramedics/", { params });
};

// Obtener listado de usuarios con el rol de "Paramédico"
export const getParamedicDriverList = (params) => {
  return axiosInstance.get("accounts/paramedics-drivers/", { params });
};

// Endpoints para las solicitudes ------------------------------------------------------
// Obtener listado de solicitudes
export const getUserRequests = (params) => {
  return axiosInstance.get("user-request/", { params });
};

// Obtener una solicitud
export const getUserRequest = (id) => {
  return axiosInstance.get(`user-request/${id}/`);
};

// Actualizar una solicitud
export const updateUserRequest = ({ id, data }) => {
  return axiosInstance.patch(`user-request/${id}/`, data);
};

// Borrar una solicitud
export const deleteUserRequest = (id) => {
  return axiosInstance.delete(`user-request/${id}/`);
};

// Obtener los estados de las solicitudes
export const getUserRequestStatus = (params) => {
  return axiosInstance.get("user-request-status/", { params });
};
// Estadísticas de las solicitudes
export const getUserRequestStats = (params) => {
  return axiosInstance.get("user-request/stats/", { params });
};

// Endpoints para las incidencias ------------------------------------------------------
// Obtener listado de incidencias
export const getIncidents = (params) => {
  const queryParams = { ...params };
  return axiosInstance.get("incidents/", { params: queryParams });
};

// Obtener una incidencia
export const getIncident = (id) => {
  return axiosInstance.get(`incidents/${id}/`);
};

// Borrar una incidencia
export const deleteIncident = (id) => {
  return axiosInstance.delete(`incidents/${id}/`);
};

// Obtener listado de tipos de incidencias
export const getIncidentTypes = (params) => {
  const queryParams = { ...params };
  return axiosInstance.get("incident-type/", { params: queryParams });
};

// Endpoints el dashboard -------------------------------------------------------------
// Obtener listado de solicitudes, de ambulancias y de áreas optimizado para el mapa
export const getMapData = (params) => {
  return axiosInstance.get("map/user-requests-and-ambulances/", { params });
};

// Endpoints para las áreas -----------------------------------------------------------
// Obtener listado de áreas
export const getAreas = (params) => {
  const queryParams = { ...params };
  return axiosInstance.get("areas/", { params: queryParams });
};
// page_size alto para obtener todas las áreas (la API pagina de 10 en 10 por defecto)
export const getAreasCoords = (params) => {
  const queryParams = { page_size: 1000 };
  if (params) queryParams.exclude = params;
  return axiosInstance.get("areas/coords/", { params: queryParams });
};

export const getAreaNames = () => {
  return axiosInstance.get("areas/names/", { params: { page_size: 1000 } });
};

// Obtener un área
export const getArea = (id) => {
  return axiosInstance.get(`areas/${id}/`);
};

// Obtener el listado de ambulancias personalizado para las áreas
export const getAmbulancesForArea = (params) => {
  return axiosInstance.get(`ambulances-for-area/`, { params });
};

// Crear un área
export const createArea = (data) => {
  return axiosInstance.post("areas/", data);
};

// Actualizar un área
export const updateArea = ({ id, data }) => {
  return axiosInstance.patch(`areas/${id}/`, data);
};

// Borrar un área
export const deleteArea = (id) => {
  return axiosInstance.delete(`areas/${id}/`);
};

// Obtener datos para los reportes
// Obtener el listado de de las solicitudes para los reportes
export const getUserRequestReport = (params) => {
  return axiosInstance.get(`user-request-report/`, { params });
};

// Obtener el listado de de las ambulancias para los reportes
export const getAmbulanceReport = (params) => {
  return axiosInstance.get(`ambulance-report/`, { params });
};

// Endpoints para el versionado de la app ------------------------------------------------------
// Obtener listado de versiones
export const getAppVersions = (params) => {
  const queryParams = { ...params };
  return axiosInstance.get("app-version/", { params: queryParams });
};

// Obtener una versión
export const getAppVersion = (id) => {
  return axiosInstance.get(`app-version/${id}/`);
};

// Crear una versión
export const createAppVersion = (data) => {
  return axiosInstance.post("app-version/", data);
};

// Actualizar una versión
export const updateAppVersion = ({ id, data }) => {
  return axiosInstance.patch(`app-version/${id}/`, data);
};

// Borrar un subtipo de servicio
export const deleteAppVersion = (id) => {
  return axiosInstance.delete(`app-version/${id}/`);
};

// Endpoints para la publicidad ------------------------------------------------------
// Obtener listado de publicidad
export const getAdvertisingList = (params) => {
  const queryParams = { ...params };
  return axiosInstance.get("advertising/", { params: queryParams });
};

// Obtener publicidad
export const getAdvertising = (id) => {
  return axiosInstance.get(`advertising/${id}/`);
};

// Crear publicidad
export const createAdvertising = (data) => {
  return axiosInstance.post("advertising/", data);
};

// Actualizar publicidad
export const updateAdvertising = ({ id, data }) => {
  return axiosInstance.patch(`advertising/${id}/`, data);
};

// Borrar publicidad
export const deleteAdvertising = (id) => {
  return axiosInstance.delete(`advertising/${id}/`);
};
